import {
  CONTROL_PLANE_ADMINISTRATION_AUDIENCE,
  CONTROL_PLANE_L3_AUDIENCE,
  refreshSession,
} from './client'
import { decodeL3TokenClaims } from './jwt'
import {
  clearL3Token,
  getAdministrationToken,
  getL3Token,
  isAdministrationTokenExpiring,
  isL3TokenExpiring,
  setAdministrationToken,
  setL3Token,
} from './tokenStore'

/**
 * RefreshSession orchestration + single-flight (Account Control Plane P5).
 *
 * RefreshSession ROTATES the family (successor rides Set-Cookie), so concurrent
 * rotations must be serialized or a second call against a consumed generation
 * REVOKES the family. We serialize with an origin-scoped Web Lock when available
 * (guards multiple console tabs sharing the cookie jar) plus intra-tab promise
 * coalescing; the freshness re-check inside the lock is the "one rotation"
 * guarantee.
 */
const REFRESH_LOCK = 'libra-console-cp-refresh'

const FIVE_MINUTES_MS = 5 * 60 * 1000

function newRotationRequestId(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (c?.randomUUID) return c.randomUUID()
  // Non-crypto fallback (older envs): uniqueness is all that matters here.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function expiresAt(jwt: string): number {
  return decodeL3TokenClaims(jwt)?.expiresAtMs || Date.now() + FIVE_MINUTES_MS
}

async function rotateUnlessFresh(): Promise<void> {
  // A concurrent caller (or peer tab holding the lock) may have already minted
  // fresh tokens; skip the rotation when both current ones are still good.
  if (!isL3TokenExpiring() && !isAdministrationTokenExpiring()) return
  const established = await refreshSession(newRotationRequestId())
  const l3 = established.accessTokens.find(t => t.audience === CONTROL_PLANE_L3_AUDIENCE)
  if (!l3?.jwt) {
    clearL3Token()
    return
  }
  setL3Token({ jwt: l3.jwt, expiresAtMs: expiresAt(l3.jwt) })
  const admin = established.accessTokens.find(
    t => t.audience === CONTROL_PLANE_ADMINISTRATION_AUDIENCE,
  )
  if (admin?.jwt) setAdministrationToken({ jwt: admin.jwt, expiresAtMs: expiresAt(admin.jwt) })
}

let inFlight: Promise<void> | null = null

export async function refreshControlPlaneSession(): Promise<void> {
  if (inFlight) return inFlight
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  inFlight = (
    locks?.request
      ? locks.request(REFRESH_LOCK, { mode: 'exclusive' }, () => rotateUnlessFresh())
      : rotateUnlessFresh()
  ).finally(() => {
    inFlight = null
  })
  return inFlight
}

/**
 * Return a non-expiring L3 bearer, rotating first if the current one is missing
 * or within the refresh skew. Used by the bearer-attach interceptor on every L3
 * request. Null when no CP session can be established (caller is anonymous).
 */
export async function ensureFreshL3Token(): Promise<string | null> {
  if (!isL3TokenExpiring()) return getL3Token()?.jwt ?? null
  try {
    await refreshControlPlaneSession()
  } catch {
    return null
  }
  return getL3Token()?.jwt ?? null
}

/**
 * Return a non-expiring administration-audience bearer for the Control Plane's
 * operator surface, rotating first if needed. Null when no CP session exists.
 */
export async function ensureFreshAdministrationToken(): Promise<string | null> {
  if (!isAdministrationTokenExpiring()) return getAdministrationToken()?.jwt ?? null
  try {
    await refreshControlPlaneSession()
  } catch {
    return null
  }
  return getAdministrationToken()?.jwt ?? null
}
